import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import Logo from 'Icons/RMlogo.png';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import { getParentPath, isNullOrUndefined } from 'utils/Global';
import {
  InspectionSetDTO,
  ProteinFormulationListDTO,
  WellImageDTO,
  SaveImageRequestDTO,
  WellDetailsState,
  ReportImageRequestDTO,
  ScoresOfWellDropsDTO,
} from 'business/models/Models';
import { TreeService } from 'business/services/TreeService';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { getRegionId } from 'components/commandpanel/Navigator/DRCIPWUtil';
import PlateService from 'business/services/PlateService';
import WellService from 'business/services/WellService';
import { Plate, WellIng } from 'components/Experiment/constants';
import { WellSelectionDetails } from 'redux/reducers/experimentDataReducer';
import { EReportTypes } from 'enums/EIconPanels';
import { createPdfWithPopupBlockerHandler, IMAGER_TEMP_PROP_NAME } from './utils';
import { WellImageWithInfoDTO } from './ReportsCommonModels';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

export const TABLE_LAYOUT = {
  hLineWidth: function (i, node) {
    return 0.5;
  },
  vLineWidth: function (i, node) {
    return 0.5;
  },
};

export async function generateWellReportPDF(
  expData: ExperimentDtoClient,
  wellSelectionDetails: WellSelectionDetails,
  saveReport: boolean,
  onPopupBlocked?: () => void,
) {
  const pdfDefinition = {
    info: {
      title: expData.expName + ' - Well Report',
      author: 'Rock Maker',
      subject: 'Experiment',
    },
    content: await CreateContent(expData, wellSelectionDetails),
    styles: {
      headerText: { fontSize: 10, bold: true, lineHeight: 1.5 },
      largeText: { fontSize: 10, lineHeight: 1.5 },
      labelText: { fontSize: 8, lineHeight: 1.5 },
      tableSubHeader: { bold: true, fillColor: '#cccccc' },
      tableAltRow: { fillColor: '#f5f5f5' },
      textBold: { bold: true },
    },
    defaultStyle: {
      fontSize: 7,
      lineHeight: 1.2,
    },
    pageMargins: [50, 50, 50, 30],
    header: getHeader(),
    pageBreakBefore: false,
  };

  createPdfWithPopupBlockerHandler(
    pdfMake,
    pdfDefinition,
    expData.expName + ' - Well Report',
    saveReport,
    onPopupBlocked,
  );
}

async function CreateContent(
  expData: ExperimentDtoClient,
  wellSelectionDetails: WellSelectionDetails,
) {
  const content = [];
  content.push('\n');
  content.push('\n');

  const wellList: WellIng[] = [];
  expData.plateDto.forEach((m) => {
    m.ingredients.wellList.forEach((wl) => {
      wellSelectionDetails.wellNavigationDetails.forEach((wd) => {
        if (wd.wellNum === wl.wellNumber && wd.plateID === wl.plateId) {
          wellList.push(wl);
        }
      });
    });
  });

  const allImages = await getDropImages(expData, wellSelectionDetails, wellList);

  for (const wl of wellSelectionDetails.wellNavigationDetails) {
    const plateInfo: any = expData.plateDto.find((plate) => plate.plateId === wl.plateID);
    const wellInfo: WellIng = expData.plateDto
      .find((plate) => plate.plateId === plateInfo.plateId)
      .ingredients.wellList.find((well) => well.wellNumber === wl.wellNum);

    content.push({
      text: 'Well Number: ' + wellInfo.rowLetter + wellInfo.columnNumber,
      style: 'headerText',
    });

    const treeService = new TreeService();
    const expParents = await treeService.getDocumentParentNodes(
      expData.experimentId,
      ETreeNodeTypes.Experiment,
      true,
    );
    const expPath = expParents ? getParentPath(expParents) : '';
    let plateIdStr = '';
    let barcodeStr = '';
    if (plateInfo.plateId > 0) {
      plateIdStr = ' - PlateID: ' + plateInfo.plateId;
    }
    if (plateInfo.plateBarName.split(',')[2]) {
      barcodeStr = ' - Barcode ID: ' + plateInfo.plateBarName.split(',')[2];
    }

    content.push({ text: 'Experiment: ' + expPath + plateIdStr + barcodeStr });

    content.push({ text: 'Plate #: ' + plateInfo.plateNumber });
    content.push('\n');
    content.push(await getWellIngredientsTable(expData, wellInfo));
    content.push('\n');
    if (wellInfo.dropList.length > 0) {
      for (const wellDrop of wellInfo.dropList) {
        if (wellDrop.proteinFormulationID !== null) {
          content.push({
            text: 'Drop Number: ' + wellDrop.dropNumber,
            style: 'headerText',
          });
          content.push(
            await getProteinInfoTable(
              expData,
              wellSelectionDetails.wellNavigationDetails,
              wellInfo,
              wellDrop.dropNumber,
              plateInfo,
            ),
          );
          content.push('\n');

          const dropProtFomulationId = wellInfo.dropList.find(
            (drp) => drp.dropNumber == wellDrop.dropNumber,
          ).proteinFormulationID;
          const proteinInfo =
            plateInfo.ingredients.proteinFormulation[dropProtFomulationId];
          content.push(await getProteinIngredientsTable(expData, proteinInfo));
          content.push('\n');
          content.push(await getStandardIngredientsTable(proteinInfo));
          content.push('\n');
          content.push({
            text:
              'Well: ' +
              wellInfo.rowLetter +
              wellInfo.columnNumber +
              ', ' +
              'Incubation Temperature: ' +
              ((await getDropImagesImagerTemperature(
                wellDrop.dropNumber,
                wl.wellNum,
                allImages,
                wellInfo,
              )) ?? '') +
              ' °C',
          });
          const selectedInspectionSet: InspectionSetDTO =
            wellSelectionDetails.selectedDropImageParameters.inspection;
          if (
            allImages?.length > 0 &&
            !isNullOrUndefined(selectedInspectionSet?.dateImaged)
          ) {
            content.push({
              text:
                'Date Imaged: ' +
                new Date(selectedInspectionSet.dateImaged).toLocaleTimeString() +
                ' ' +
                new Date(selectedInspectionSet.dateImaged).toLocaleDateString('en-US'),
              bold: true,
            });
            content.push('\n');
            content.push(
              await getDropImagesTable(
                wellDrop.dropNumber,
                wl.wellNum,
                expData,
                allImages,
                wellInfo,
              ),
            );
          }
          content.push({ text: '\n', pageBreak: 'after' });
        }
      }
    }

    content.push('\n');
  }

  return content;
}

async function getProteinInfoTable(
  expData: ExperimentDtoClient,
  wellDetails: any[],
  wellInfo: WellIng,
  dropNumber: number,
  plateInfo: Plate,
) {
  const dropProtFomulationId = wellInfo.dropList.find(
    (drp) => drp.dropNumber == dropNumber,
  ).proteinFormulationID;
  const proteinInfo = plateInfo.ingredients.proteinFormulation[dropProtFomulationId];
  return {
    table: {
      widths: ['34%', '33%', '33%'],
      body: [
        [
          {
            text: 'Protein Formulation Details',
            bold: true,
            colSpan: 3,
            fillColor: '#808080',
            color: 'white',
            style: 'labelText',
          },
          {},
          {},
        ],
        [
          { text: 'ID: ' + dropProtFomulationId },
          { text: 'Protein Lot ID: ' + proteinInfo.proteinInfo.proteinLotId },
          { text: 'Liquid Class: ' + proteinInfo.proteinInfo.liquidClassName },
        ],
        [
          { text: 'Class: ' + proteinInfo.proteinInfo.pclass },
          {
            text:
              'Date Prepared: ' +
              new Date(proteinInfo.proteinInfo.datePrepared).toLocaleDateString('en-US'),
          },
          { text: 'Ext. Coefficient: ' + proteinInfo.proteinInfo.extCoefficient },
        ],
        [
          { text: 'Volume (uL): ' + proteinInfo.proteinInfo.volume },
          { text: 'Storage Temperature (C): ' + proteinInfo.proteinInfo.storageTemp },
          { text: 'Notebook Page No: ' + proteinInfo.proteinInfo.noteBookPageNo },
        ],
      ],
    },
    layout: TABLE_LAYOUT,
  };
}

async function getProteinIngredientsTable(
  expData: ExperimentDtoClient,
  proteinInfo: ProteinFormulationListDTO,
) {
  const body: any = [
    [
      {
        text: 'Protein Formulation Ingredients',
        bold: true,
        colSpan: 7,
        fillColor: '#808080',
        style: 'labelText',
        color: 'white',
      },
      {},
      {},
      {},
      {},
      {},
      {},
    ],
    [
      { text: 'Name', bold: true, fillColor: '#cccccc' },
      { text: 'Type', bold: true, fillColor: '#cccccc' },
      { text: 'ID', bold: true, fillColor: '#cccccc' },
      { text: 'Lot', bold: true, fillColor: '#cccccc' },
      { text: 'MW (kDa)', bold: true, fillColor: '#cccccc' },
      { text: 'Conc. (mg/mL)', bold: true, fillColor: '#cccccc' },
      { text: 'Conc. (mM)', bold: true, fillColor: '#cccccc' },
    ],
  ];
  proteinInfo.ingredients.forEach((p) => {
    body.push([
      p.name,
      p.typeName,
      p.id,
      p.lot,
      p.moleculerWeight,
      p.concentrationmgml,
      p.concentrationmM,
    ]);
  });
  return {
    table: {
      widths: ['16%', '14%', '14%', '14%', '14%', '14%', '14%'],
      body: body,
    },
    layout: TABLE_LAYOUT,
  };
}

async function getStandardIngredientsTable(proteinInfo: ProteinFormulationListDTO) {
  const body: any = [
    [
      {
        text: 'Protein Formulation Standard Ingredients',
        bold: true,
        colSpan: 8,
        fillColor: '#808080',
        style: 'labelText',
        color: 'white',
      },
      {},
      {},
      {},
      {},
      {},
      {},
      {},
    ],
    [
      { text: 'Name', bold: true, fillColor: '#cccccc' },
      { text: 'Type', bold: true, fillColor: '#cccccc' },
      { text: 'Stock Conc.', bold: true, fillColor: '#cccccc' },
      { text: 'Conc. Units', bold: true, fillColor: '#cccccc' },
      { text: 'Stock Low pH', bold: true, fillColor: '#cccccc' },
      { text: 'Stock High pH', bold: true, fillColor: '#cccccc' },
      { text: 'Conc.', bold: true, fillColor: '#cccccc' },
      { text: 'pH', bold: true, fillColor: '#cccccc' },
    ],
  ];
  proteinInfo.standardIngredients.forEach((p) => {
    body.push([
      p.ingredientName,
      p.type,
      p.stockConcentration,
      p.stockConcentrationUnit,
      p.stockpH,
      p.highpHStockpH,
      p.concentrationM,
      p.pH,
    ]);
  });
  return {
    table: {
      widths: ['25%', '15%', '10%', '10%', '10%', '10%', '10%', '10%'],
      body: body,
    },
    layout: TABLE_LAYOUT,
  };
}

async function getWellIngredientsTable(expData: ExperimentDtoClient, wellInfo: WellIng) {
  const body: any = [
    [
      {
        text: 'Well Condition',
        bold: true,
        fillColor: '#808080',
        color: 'white',
        style: 'labelText',
        colSpan: 4,
      },
      {},
      {},
      {},
    ],
    [
      { text: 'Type', bold: true, fillColor: '#cccccc' },
      { text: 'Name', bold: true, fillColor: '#cccccc' },
      { text: 'Conc.', bold: true, fillColor: '#cccccc' },
      { text: 'pH', bold: true, fillColor: '#cccccc' },
    ],
  ];
  wellInfo.wellLayerList.forEach((wlaylist) => {
    wlaylist.wellLayerIngredientList.forEach((layIng) => {
      body.push([
        layIng.ingredientTypeName,
        layIng.ingredientStockIngredient.longName,
        layIng.concentration + layIng.ingredientStock.concentrationUnits,
        layIng.pH,
      ]);
    });
  });

  if (wellInfo.wellLayerList.length > 0) {
    return {
      table: {
        widths: ['20%', '40%', '20%', '20%'],
        body: body,
      },
      layout: TABLE_LAYOUT,
    };
  } else {
    return null;
  }
}

function getDropImagesImagerTemperature(
  dropNumber: number,
  wellNumber: number,
  allImages: WellImageWithInfoDTO[],
  wellInfo: WellIng,
) {
  const dropImages = allImages.filter(
    (image) =>
      image.wellNumber === wellNumber &&
      image.dropNumber === dropNumber &&
      image.wellInfo.plateId === wellInfo.plateId,
  );
  return dropImages
    ?.flatMap((img) => img.captureResultProperties || [])
    .find((p) => p.name === IMAGER_TEMP_PROP_NAME)?.value;
}

async function getDropImagesTable(
  dropNumber: number,
  wellNumber: number,
  expData: ExperimentDtoClient,
  allImages: WellImageWithInfoDTO[],
  wellInfo: WellIng,
) {
  const rows: any = [];
  let columns: any = [];
  let colCount = 1;

  const dropImages = allImages.filter(
    (image) =>
      image.wellNumber === wellNumber &&
      image.dropNumber === dropNumber &&
      image.wellInfo.plateId === wellInfo.plateId,
  );
  if (dropImages?.length > 0) {
    dropImages.forEach((dImg) => {
      if (dImg.wellImage !== null) {
        dImg.wellInfo = wellInfo;

        const color = '#FFFFFF';
        let score;

        columns.push([
          {
            margin: [20, 0, 0, 0],
            table: {
              headerRows: 3,
              body: [
                [
                  {
                    table: {
                      widths: ['auto', 400],
                      body: [
                        [
                          {
                            border: [false, false, false, false],
                            fillColor: color,
                            text: score,
                          },
                          {
                            border: [false, false, false, false],
                            text:
                              dImg.wellInfo.rowLetter +
                              dImg.wellInfo.columnNumber +
                              '-' +
                              dImg.dropNumber,
                            bold: true,
                            alignment: 'center',
                          },
                        ],
                      ],
                    },
                  },
                ],

                [{ image: dImg.wellImage, width: 440, height: 360 }],
                [
                  {
                    text:
                      new Date(dImg.inspectionSet.dateImaged).toLocaleTimeString() +
                      ' ' +
                      new Date(dImg.inspectionSet.dateImaged).toLocaleDateString('en-US'),
                    alignment: 'center',
                  },
                ],
              ],
            },
          },
        ]);
      } else {
        columns.push([{ text: 'No image found', bold: true }]);
      }
      if (colCount == dropImages.length || (colCount > 0 && colCount % 2 == 0)) {
        rows.push({
          alignment: 'justify',
          width: '*',
          columns: columns,
        });
        rows.push('\n');
        columns = [];
      }
      colCount++;
    });
  }
  return rows;
}

async function getDropImages(
  expData: ExperimentDtoClient,
  wellSelectionDetails: WellSelectionDetails,
  wellInfos: WellIng[],
): Promise<WellImageWithInfoDTO[]> {
  const result: WellImageWithInfoDTO[] = [];
  const selectedInspection: InspectionSetDTO =
    wellSelectionDetails.selectedDropImageParameters.inspection;
  const wellService = new WellService();
  const plates: Plate[] = [];
  const uniquePlateIds = new Set<number>();
  for (const well of wellSelectionDetails.wellNavigationDetails) {
    uniquePlateIds.add(well.plateID);
  }
  const dropNumbers = new Set<number>();
  for (const drop of expData.containerDrops) {
    dropNumbers.add(drop.dropNumber);
  }

  for (const plateId of [...uniquePlateIds]) {
    plates.push(expData.plateDto.find((plate) => plate.plateId === plateId));
  }

  const imageDto: SaveImageRequestDTO = new SaveImageRequestDTO();
  imageDto.saveAllRegions = true;
  imageDto.saveAllWellDropScores = true;
  imageDto.saveAnnotations = true;
  imageDto.selectedCaptureProfileIds = [];
  imageDto.selectedCaptureProfileIds.push(
    wellSelectionDetails.selectedDropImageParameters.cpId,
  );
  imageDto.inspectionSetDTOs = [];
  imageDto.saveAllDrops = false;
  imageDto.saveAllFocusLevels = false;
  imageDto.saveAllInspections = false;
  imageDto.wellDetails = [];
  for (const well of wellSelectionDetails.wellNavigationDetails) {
    const wellInfo = expData.plateDto
      .find((plate) => plate.plateId === well.plateID)
      .ingredients.wellList.find(
        (ingredientWell) => ingredientWell.wellNumber === well.wellNum,
      );

    for (const dropNum of dropNumbers) {
      const wellDetailState = new WellDetailsState();
      wellDetailState.cpID = wellSelectionDetails.selectedDropImageParameters.cpId;
      wellDetailState.dropNumber = dropNum;
      wellDetailState.imageBatchId = selectedInspection.groupedInspections.find(
        (x) => x.plateId === well.plateID,
      )?.imageBatchId;
      wellDetailState.plateID = well.plateID;
      wellDetailState.regionID = getRegionId(
        expData.regions,
        well.wellNum,
        dropNum,
        'Drop',
        well.plateID,
      );
      wellDetailState.regionName = 'Drop';
      wellDetailState.wellName = wellInfo.rowLetter + wellInfo.wellNumber;
      wellDetailState.wellNum = wellInfo.wellNumber;
      imageDto.wellDetails.push(wellDetailState);
    }
  }

  const reportImageRequest = new ReportImageRequestDTO();
  reportImageRequest.reportType = EReportTypes.WellReport;
  reportImageRequest.saveImageRequestDTO = imageDto;
  const dropImages: WellImageDTO[] =
    await wellService.getWellImagesWithDrawingsAnnotations(reportImageRequest);

  const plateScores = new Map<number, ScoresOfWellDropsDTO[][]>();
  for (const plate of uniquePlateIds) {
    plateScores[plate] = await getPlateScores(plate);
  }

  for (const dropImage of dropImages) {
    const newImage = new WellImageWithInfoDTO(dropImage);

    if (!newImage.key) {
      continue;
    }

    newImage.inspectionSet = selectedInspection;
    newImage.isSelectedInspection = true;

    const splitKey = newImage.key.split('-');
    const plateId = Number(splitKey[0]);
    const wellNumber = Number(splitKey[1]);
    const dropNumber = Number(splitKey[2]);
    const batchId = Number(splitKey[3]);
    newImage.dropNumber = dropNumber;

    let score: ScoresOfWellDropsDTO | undefined;
    plateScores[plateId].some(
      (wellScores) =>
        (score = wellScores.find(
          (wellScore) =>
            wellScore.wellNumber === wellNumber &&
            wellScore.dropNumber === dropNumber &&
            wellScore.scoreBatchId === batchId,
        )) !== undefined,
    );
    newImage.score = score;

    newImage.wellInfo = wellInfos.find(
      (wellInfo) => wellInfo.wellNumber === wellNumber && wellInfo.plateId === plateId,
    );

    result.push(newImage);
  }

  return result;
}

async function getPlateScores(plateId: number): Promise<ScoresOfWellDropsDTO[][]> {
  const plateService = new PlateService();
  return await plateService.getPlateScoreBasedOnWellsByPlateId(plateId);
}

function getHeader() {
  return [
    {
      columns: [
        {
          image: Logo,
          width: 20,
          height: 20,
        },
        {
          text: 'ROCK MAKER®',
          margin: [5, 4, 0, 0],
          width: 440,
          fontSize: 12,
          color: '#707070',
        },
        {
          text: 'Printed On: ' + new Date().toLocaleDateString('en-US'),
          margin: [0, 5, 0, 0],
          color: '#707070',
        },
      ],
      margin: [20, 20, 20, 20],
    },
  ];
}
